<template>
  <div class="col-12 meta-data-updater">
    <div class="form-section col-12 pb-1 mb-1">
      <h3>{{ $t("UpdateMetaData") }}</h3>
      <p class="lighter mb-1">{{ $t('metaDataUpdaterHint') }}</p>
    </div>
    <div>
      <meta-data-upload
          ref="metaDataUpload"
          :asset-id="$route.params.id"
          :allow-json="true"
          :validation-methods-for-col-names="[validateColNames]"
          class="mt-3 col-12"
          @fileReceived="fileReceived"
          @metaDataColumns="setMetaDataColumns"
          @partIdentifierColumn="(val) => {metaConfig.partIdentifierColumn = val}"
          @reset="reset"
          @setVhubPreProcessor="setVhubPreProcessor"
      />
      <div v-if="metaError" class="p-2 form-error mt-2 mb-2 ml-3">
        {{ metaError }}
      </div>
      <div v-if="metaConfig.partIdentifierColumn && !metaError" class="col-12 mt-2">
        <meta-set-selector
            v-if="$store.getters.getAssemblyOrganization($route.params.id)"
            ref="metaSelect"
            :can-create-new="true"
            :exclude="['sfx.asset.model']"
            :meta-set-prefix="'asset.model.' + $store.getters.getOrganizationName($store.getters.getAssemblyOrganization($route.params.id))"
            :organization-id="$store.getters.getAssemblyOrganization($route.params.id)"
            :show-save-button="false"
            :teams="$store.getters.getAssemblyTeams($route.params.id)"
            permission="writeMetaSet"
            @update="setMetaSet"
        />
      </div>
      <div
          v-if="metaConfig && metaConfig.metaSet.id"
          class="col-12 mt-3">
        <label class="lighter mb-0">4 {{ $t("Modus") }} </label>
        <info-helper class="ml-1" text="MetaUpdateMode"/>
        <tile-button
            ref="tiles"
            :initial-active-tiles="[metaSetUpdateModes[0]]"
            :tiles="metaSetUpdateModes"
            :values="metaSetUpdateModes"
            @trigger="selectMode"
        />
      </div>
      <regex-selector
          v-if="metaConfig && metaConfig.metaSet.id"
          class="col-12"
          @setRegexes="setRegexes"
      />
      <div
          v-if="metaConfig && metaConfig.metaSet.id"
          class="col-12 mt-3"
      >
        <label class="lighter mb-0">5 {{ $t("email") }}</label>
        <input
            v-model="email"
            :class="['form-text form-text-dark w-100', $v.email.$dirty && $v.email.$error ? 'form-group--error' : '',]"
            type="text"
            @input="delayTouch($v.email)"
        />
        <div
            v-if="$v.email.$dirty && !$v.email.email"
            class="form-error mb-2"
        >
          {{ $t("errors.mustBeAValidEmailAdress") }}
        </div>
        <div v-if="error" class="p-2 form-error mt-2 mb-2">
          {{ error }}
        </div>
        <div
            v-if="!showSpinner"
            :deactivated="!this.$v.email.email || error"
            class="settings-button d-inline-block mt-3 icon-rotate-animation make-longer-animation"
            @click="startMetaSetUpdate"
        >{{ $t("XMLUpdateStart") }}
          <icon class="ml-1" type="cube"/>
        </div>
      </div>
      <div v-if="showSpinner" class="ml-3 mt-2 message">
        <span>{{ $t("metaDataUpdateTaskStarting") }}</span>
        <loading-spinner/>
      </div>
      <div v-if="showProcessingMessage" class="ml-3 mt-2">
        <span>{{ $t("metaDataUpdateTaskStarted") }} </span>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from "../Icon";
import TileButton from "@/components/forms/TileButton";
import InfoHelper from "@/components/InfoHelper";
import VuelidateMixin from "@/components/mixins/VuelidateMixin.js";
import {metaDataFileFormats, metaSetUpdateModes, TaskManagerTaskTypes} from "@/enum";
import {email} from "vuelidate/lib/validators";
import MetaDataUpload from "@/components/assemblies/MetaDataUpload.vue";
import MetaSetSelector from "@/components/widgets/meta/MetaSetSelector.vue";
import RegexSelector from "@/components/assemblies/RegexSelector.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";

export default {
  name: "MetaDataUpdater",
  components: {
    LoadingSpinner,
    RegexSelector,
    MetaSetSelector,
    TileButton,
    InfoHelper,
    Icon,
    MetaDataUpload
  },
  mixins: [VuelidateMixin],
  props: {
    remoteContent: {type: Object, required: true},
    canUpdate: {type: Boolean, default: false}
  },
  data() {
    return {
      /**
       * the columns from the meta file (which is usually a table of some sort)
       * */
      metaDataColumns: [],
      /**
       * Sets the dropzone to loading-state while processing data
       * */
      isLoading: false,
      /**
       * the available file formats = processors for meta-files (currently Windchill Search Res only)
       * */
      metaDataFileFormats: metaDataFileFormats,
      vhubPreProcessorOptions: metaDataFileFormats,

      /**
       * The preprocessor will be set as
       * */
      vhubPreProcessor: "",

      /**
       * the mode to use for either overwriting/extending or reset the meta-data
       * */
      mode: "extend",
      metaSetUpdateModes: metaSetUpdateModes,

      email: this.$store.getters.getUserEmail(this.$store.getters.getCurrentUserId),

      content: {},
      dataProcessor: null,
      error: '',
      metaError: '',
      files: [],
      regexes: [],
      // this is how the pipeline expects the metaConfig
      metaConfig: {
        partIdentifierColumn: '',
        metaSet: {
          name: null,
          id: null
        }
      },
      showSpinner: false,
      showProcessingMessage: false,
    };
  },
  validations: {
    email: {
      email,
    },
  },
  methods: {
    validateColNames(items) {
      const $this = this;
      this.metaError = '';
      for(let i = 0; i < items.length; i++) {
        const item = items[i];
        if(item.length < 3) {
          $this.metaError = 'You have column names that are less than 3 characters. Please correct your data and try again.'
        }
      }
    },
    setRegexes(regexes) {
      this.regexes = regexes && regexes.length ? regexes.map(item => {
        return {
          id: item.id,
          content: item.content
        }
      }) : [];
    },
    setMetaSet(metaSetName, metaSetId) {
      this.metaConfig.metaSet.name = metaSetName;
      this.metaConfig.metaSet.id = metaSetId;
    },
    setVhubPreProcessor(vhubPreProcessor) {
      this.vhubPreProcessor = vhubPreProcessor;
    },
    // 1 resume upload-dropzone upload which triggers 2
    startMetaSetUpdate() {
      this.error = '';
      this.showSpinner = true;
      this.$refs.metaDataUpload.resumeUpload();
    },
    // 2 when file is uploaded, start update of sfx content and
    // 3 start the pipeline task "dataprocessing"
    async fileReceived(assetId, fileName) {
      this.error = '';
      const status = await this.$store.dispatch('clientGetPipelineStatus', {
        id: this.$route.params.id
      }).catch(async (err) => {
        if (err.statusCode === 404) {
          await this.updateAssemblyContent(fileName);
          await this.triggerPipelineTask();
          this.showProcessingMessage = true;
          this.$emit('triggered');
          return;
        } else {
          this.error = err.status.message;
        }
      });
      if (status) {
        this.error = this.$t('pipelineIsProcessing');
      }
    },
    async triggerPipelineTask() {
      return await this.$store.dispatch("clientAssetToPipeline", {
        id: this.$route.params.id,
        type: TaskManagerTaskTypes.DATAPROCESSING,
        config: JSON.stringify({
          ...this.content.pipeline,
          sendMails: true,
          email: this.email,
          lang: this.$i18n.locale ? this.$i18n.locale : 'en',
        })
      }).catch(err => {
        this.error = err.status.message;
        throw new Error('Error while triggering pipeline task');
      });
    },
    setMetaDataColumns(data) {
      this.metaDataColumns = data;
    },
    reset() {
      this.metaConfig.partIdentifierColumn = '';
      this.metaConfig.metaSet.id = null;
      this.metaConfig.metaSet.name = null;
    },
    async updateAssemblyContent(clientMetaFile) {
      if (!this.metaConfig.metaSet.id || this.metaConfig.metaSet.id === 999) {
        if (this.$refs.metaSelect) {
          const newMetaSet = await this.$refs.metaSelect.createNewMetaSet();
          this.metaSetId = newMetaSet.id;
          this.metaSetName = newMetaSet.name;
        }
      }
      this.content.pipeline = {
        files: [`${this.$route.params.id}/${clientMetaFile}`],
        sendMails: true,
        email: this.email,
        lang: this.$i18n.locale ? this.$i18n.locale : "en",
        dataProcessor: this.vhubPreProcessor,
        meta: {
          updateMode: this.mode,
          clientMetaFile: clientMetaFile,
          regexes: this.regexes,
          ...this.metaConfig,
        }
      }
      return await this.$store.dispatch("loadAssembly", {
        id: this.$route.params.id
      }).then(async () => {
        return await this.$store.dispatch("updateAssembly", {
          id: this.$route.params.id,
          content: JSON.stringify(this.content),
        });
      });
    },
    selectMode(params) {
      this.mode = params.value;
    },
  }
}
</script>

<style scoped>
.meta-data-updater {
  max-width: 600px;
}
</style>
